import { useMemo, useState } from 'react';
import { ShoppingCart, Download, Check, Undo2, AlertCircle, ListFilter } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { Skeleton } from '../../components/ui/Skeleton';
import {
  useKitOrders,
  useMarkOrdered,
  useUnmarkOrdered,
  type KitOrderBoard,
  type OrderLine,
  type OrderUnit,
  type PersonOrderRow,
} from '../../hooks/useKitOrders';
import { ApiError } from '../../lib/api';

type Tab = 'line' | 'person';

/** Demand units of an item+size at a given ordered state, drawn from the people view. */
function unitsForLine(board: KitOrderBoard, line: OrderLine, ordered: boolean): OrderUnit[] {
  const out: OrderUnit[] = [];
  for (const p of board.people) {
    for (const it of p.items) {
      if (it.item === line.item && it.size === line.size && it.ordered === ordered) {
        out.push({ person_id: p.person_id, item_name: it.item, size: it.size });
      }
    }
  }
  return out;
}

export function KitOrders() {
  const [tab, setTab] = useState<Tab>('line');
  const board = useKitOrders();

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
        <PageHeader
          eyebrow="Pre-day"
          eyebrowIcon={ShoppingCart}
          title="Kit to order"
          description="Uniform and PPE to buy, totalled live from everyone's submitted sizes. Mark lines as you place orders; late submissions show as new outstanding demand."
          trailing={
            <a href="/api/admin/kit-orders/export.xlsx">
              <Button variant="secondary" size="sm" iconLeft={<Download className="h-4 w-4" />}>
                Export
              </Button>
            </a>
          }
        />

        <div className="mt-4 inline-flex rounded-token-sm border border-line/10 bg-surface p-0.5 text-sm">
          {(['line', 'person'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`rounded-[6px] px-3 py-1.5 font-medium transition-colors ${
                tab === t ? 'bg-accent text-fg-on-accent' : 'text-fg-muted hover:text-fg'
              }`}
            >
              {t === 'line' ? 'By line (what to buy)' : 'By person (who is covered)'}
            </button>
          ))}
        </div>

        <div className="mt-4">
          {board.isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : board.error ? (
            <div className="flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{(board.error as Error).message}</span>
            </div>
          ) : !board.data || board.data.lines.length === 0 ? (
            <EmptyState
              icon={<ShoppingCart className="h-6 w-6" />}
              title="Nothing to order yet"
              description="Once inductees submit a uniform/PPE sizing form whose rows are tagged as orderable items, their sizes total up here."
            />
          ) : tab === 'line' ? (
            <ByLine board={board.data} />
          ) : (
            <ByPerson board={board.data} />
          )}
        </div>
      </div>
    </main>
  );
}

function ByLine({ board }: { board: KitOrderBoard }) {
  const mark = useMarkOrdered();
  const unmark = useUnmarkOrdered();
  const busy = mark.isPending || unmark.isPending;

  const totalOutstanding = board.lines.reduce((s, l) => s + l.outstanding, 0);

  function onMark(line: OrderLine) {
    const units = unitsForLine(board, line, false);
    if (units.length === 0) return;
    mark.mutate(units, {
      onSuccess: () => toast.success(`Marked ${units.length} ${line.item} (${line.size}) ordered`),
      onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
    });
  }
  function onUndo(line: OrderLine) {
    const units = unitsForLine(board, line, true);
    if (units.length === 0) return;
    unmark.mutate(units, {
      onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
    });
  }

  return (
    <Card>
      <div className="flex items-center justify-between border-b border-line/10 px-4 py-3 text-sm">
        <span className="font-medium text-fg">{board.lines.length} lines</span>
        <span className="text-fg-muted">
          {totalOutstanding} item{totalOutstanding === 1 ? '' : 's'} still to order
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line/10 text-left text-xs uppercase tracking-wider text-fg-muted">
              <th className="px-4 py-2 font-medium">Item</th>
              <th className="px-2 py-2 font-medium">Size</th>
              <th className="px-2 py-2 text-right font-medium">Needed</th>
              <th className="px-2 py-2 text-right font-medium">Ordered</th>
              <th className="px-2 py-2 text-right font-medium">Outstanding</th>
              <th className="px-4 py-2 text-right font-medium">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-divider/10">
            {board.lines.map((l) => (
              <tr key={`${l.item}__${l.size}`} className="align-middle">
                <td className="px-4 py-2.5">
                  <span className="font-medium text-fg">{l.item}</span>
                  <Pill tone={l.category === 'PPE' ? 'info' : 'accent'} className="ml-2">
                    {l.category}
                  </Pill>
                </td>
                <td className="px-2 py-2.5 font-mono text-fg">{l.size}</td>
                <td className="px-2 py-2.5 text-right tabular-nums text-fg">{l.needed}</td>
                <td className="px-2 py-2.5 text-right tabular-nums text-fg-muted">{l.ordered}</td>
                <td className="px-2 py-2.5 text-right">
                  {l.outstanding === 0 ? (
                    <Pill tone="success">Ordered</Pill>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="tabular-nums font-medium text-fg">{l.outstanding}</span>
                      {l.new_since_last_order > 0 ? (
                        <Pill tone="warning">+{l.new_since_last_order} new</Pill>
                      ) : null}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right">
                  {l.outstanding > 0 ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={busy}
                      iconLeft={<Check className="h-3.5 w-3.5" />}
                      onClick={() => onMark(l)}
                    >
                      Mark ordered
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      iconLeft={<Undo2 className="h-3.5 w-3.5" />}
                      onClick={() => onUndo(l)}
                    >
                      Undo
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ByPerson({ board }: { board: KitOrderBoard }) {
  const mark = useMarkOrdered();
  const unmark = useUnmarkOrdered();
  const busy = mark.isPending || unmark.isPending;
  const [onlyOutstanding, setOnlyOutstanding] = useState(false);

  const outstandingPeople = useMemo(
    () => board.people.filter((p) => p.any_outstanding).length,
    [board.people],
  );
  const people = onlyOutstanding
    ? board.people.filter((p) => p.any_outstanding)
    : board.people;

  // Same endpoints as the line view, batched the other way round: every
  // not-yet-ordered unit for one person instead of every person for one line.
  function personUnits(p: PersonOrderRow, ordered: boolean): OrderUnit[] {
    return p.items
      .filter((it) => it.ordered === ordered)
      .map((it) => ({ person_id: p.person_id, item_name: it.item, size: it.size }));
  }
  function onMarkPerson(p: PersonOrderRow) {
    const units = personUnits(p, false);
    if (units.length === 0) return;
    mark.mutate(units, {
      onSuccess: () =>
        toast.success(
          `Marked ${units.length} item${units.length === 1 ? '' : 's'} ordered for ${p.person_name}`,
        ),
      onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
    });
  }
  function onUndoPerson(p: PersonOrderRow) {
    const units = personUnits(p, true);
    if (units.length === 0) return;
    unmark.mutate(units, {
      onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
    });
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-fg-muted">
        <span>
          {board.people.length} with sizes submitted · {outstandingPeople} with kit still to
          order
        </span>
        <button
          type="button"
          aria-pressed={onlyOutstanding}
          onClick={() => setOnlyOutstanding((v) => !v)}
          className={`inline-flex items-center gap-1.5 rounded-token-sm border px-2.5 py-1 text-xs font-medium transition-colors ${
            onlyOutstanding
              ? 'border-accent/30 bg-accent/10 text-accent'
              : 'border-line-strong/20 bg-surface text-fg-muted hover:text-fg'
          }`}
        >
          <ListFilter className="h-3.5 w-3.5" />
          Kit still to order
        </button>
      </div>
      {people.length === 0 ? (
        <div className="rounded-token-sm border border-line/10 bg-surface px-4 py-6 text-center text-sm text-fg-muted">
          Everyone's kit has been ordered.
        </div>
      ) : null}
      {people.map((p) => (
        <Card key={p.person_id}>
          <div className="flex items-center justify-between gap-2 border-b border-line/10 px-4 py-2.5">
            <span className="font-medium text-fg">{p.person_name}</span>
            <span className="inline-flex items-center gap-2">
              {p.any_outstanding ? (
                <>
                  <Pill tone="warning">Kit to order</Pill>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={busy}
                    iconLeft={<Check className="h-3.5 w-3.5" />}
                    onClick={() => onMarkPerson(p)}
                  >
                    Mark ordered
                  </Button>
                </>
              ) : (
                <>
                  <Pill tone="success">All ordered</Pill>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    iconLeft={<Undo2 className="h-3.5 w-3.5" />}
                    onClick={() => onUndoPerson(p)}
                  >
                    Undo
                  </Button>
                </>
              )}
            </span>
          </div>
          <div className="flex flex-wrap gap-2 px-4 py-3">
            {p.items.map((it) => (
              <span
                key={`${it.item}__${it.size}`}
                className={`inline-flex items-center gap-1.5 rounded-token-sm border px-2 py-1 text-xs ${
                  it.ordered
                    ? 'border-success-soft/25 bg-success-soft/10 text-success-soft-fg'
                    : 'border-line-strong/20 bg-surface-2 text-fg'
                }`}
              >
                {it.ordered ? <Check className="h-3 w-3" /> : null}
                <span className="font-medium">{it.item}</span>
                <span className="font-mono text-fg-muted">{it.size}</span>
                <span className="text-fg-dim">×{it.quantity}</span>
              </span>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
